import { GoogleMap } from "@react-google-maps/api";
import { useState, useCallback, useEffect } from "react";
import { WorkspaceCardData } from "./Interfaces/WorkspaceCard";

// Extend Window interface to include viewWorkspaceDetails
declare global {
  interface Window {
    viewWorkspaceDetails: (workspaceId: number) => void;
  }
}

const mapContainerStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  minHeight: "400px",
};

interface WorkspaceMapProps {
  center?: { lat: number; lng: number };
  workspaces: WorkspaceCardData[];
  onWorkspaceSelect?: (workspace: WorkspaceCardData) => void;
}

interface WorkspaceMarker extends WorkspaceCardData {
  position?: { lat: number; lng: number };
  marker?: google.maps.marker.AdvancedMarkerElement;
}

export default function WorkspaceMap({
  center = { lat: -25.2744, lng: 133.7751 }, // Default to center of Australia
  workspaces,
  onWorkspaceSelect,
}: WorkspaceMapProps) {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [workspaceMarkers, setWorkspaceMarkers] = useState<WorkspaceMarker[]>(
    []
  );

  const onLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Geocode workspace addresses and create markers
  useEffect(() => {
    if (!map || workspaces.length === 0) return;

    // Store markers in a local variable for cleanup
    const currentMarkers: WorkspaceMarker[] = [];
    console.log(workspaceMarkers);
    const geocoder = new google.maps.Geocoder();
    const infoWindowInstance = new google.maps.InfoWindow();

    const geocodePromises = workspaces.map(
      (workspace) =>
        new Promise<WorkspaceMarker>((resolve) => {
          // Build full address for geocoding
          const fullAddress = [
            workspace.address,
            workspace.city,
            workspace.state,
            workspace.postalCode,
            workspace.country,
          ]
            .filter(Boolean)
            .join(", ");

          geocoder.geocode(
            { address: fullAddress },
            (results, status) => {
              console.log(`Geocoding "${fullAddress}": ${status}`);
              if (status === "OK" && results && results[0]) {
                const position = {
                  lat: results[0].geometry.location.lat(),
                  lng: results[0].geometry.location.lng(),
                };

                // Create marker content
                const markerContent = document.createElement("div");
                markerContent.innerHTML = `
              <div style="
                background: #f53636;
                border: 1px solid white;
                border-radius: 50%;
                width: 16px;
                height: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 2px 6px rgba(0,0,0,0.3);
              ">
              </div>
            `;

                // Create AdvancedMarkerElement
                const marker = new google.maps.marker.AdvancedMarkerElement({
                  map: map,
                  position: position,
                  content: markerContent,
                  title: workspace.title,
                });

                // Add click listener
                marker.addListener("click", () => {
                  infoWindowInstance.setContent(`
                <div style="padding: 8px; max-width: 250px; border-radius: 8px; ">
                  <button
                    style="background: none; border: none; width: 100%; cursor: pointer;"
                    onclick="window.viewWorkspaceDetails(${
                      workspace.workspaceId
                    })"
                  >
                    <img
                      src=${workspace.images[0]?.imageUrl}
                      style="width: 100%; height: auto; border-radius: 8px; margin-bottom: 8px;"
                    />
                    <h3 style="font-weight: 600; font-size: 14px; margin-bottom: 4px;">${
                      workspace.title
                    }</h3>
                    <p style="font-size: 12px; color: #666; margin-bottom: 8px;">${
                      workspace.address
                    }</p>
                    <div style=" margin-bottom: 8px;">
                      <span style="font-weight: bold; font-size: 14px;">$${workspace.pricePerYear.toLocaleString()}/year</span>
                    </div>
                    <div>
                    <span style="font-size: 12px; color: #666;">${
                      workspace.capacity
                    } people</span>
                    </div>
                  </button>
                </div>
              `);
                  infoWindowInstance.open(map, marker);
                });

                const workspaceMarker = {
                  ...workspace,
                  position,
                  marker,
                };
                currentMarkers.push(workspaceMarker);
                resolve(workspaceMarker);
              } else {
                // If geocoding fails, exclude this workspace from markers
                console.warn(`Geocoding failed for "${fullAddress}": ${status}`);
                resolve(workspace);
              }
            }
          );
        })
    );

    Promise.all(geocodePromises).then((markers) => {
      const validMarkers = markers.filter(
        (marker) => marker.position && marker.marker
      );
      setWorkspaceMarkers(validMarkers);
    });

    // Global function for workspace details
    window.viewWorkspaceDetails = (workspaceId: number) => {
      const workspace = workspaces.find((w) => w.workspaceId === workspaceId);
      if (workspace && onWorkspaceSelect) {
        onWorkspaceSelect(workspace);
      }
      window.open(`/workspace/${workspaceId}`, "_blank");
    };

    // Cleanup function - use local variable instead of state
    return () => {
      currentMarkers.forEach((marker) => {
        if (marker.marker) {
          marker.marker.map = null;
        }
      });
    };
  }, [workspaces, map, onWorkspaceSelect]);

  // Update map center when location changes
  useEffect(() => {
    if (map && center) {
      map.setCenter(center);
      // Use different zoom levels: 5 for Australia default, 12 for specific locations
      const isDefaultCenter =
        center.lat === -25.2744 && center.lng === 133.7751;
      map.setZoom(isDefaultCenter ? 5 : 12);
    }
  }, [center, map]);

  return (
    <div className="h-full w-full">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={5}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          mapId: "DEMO_MAP_ID", // Required for AdvancedMarkerElement
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        }}
      >
        {/* Markers are now created programmatically using AdvancedMarkerElement */}
      </GoogleMap>
    </div>
  );
}
